import { bigintMin, } from "./helpers";
import type { Lot, InputTransaction, PnLTransaction, } from "@types";
import { formatUnits, parseUnits, } from "viem";




// Accumulator declare type for the .reduce() method
declare type ReduceAccumulator = {
	processedTransactions: PnLTransaction[];
	openLotsByVault: Record<string, Lot[]>; // Key: ticker, Value: array of open lots (FIFO order)
}


export declare type CalculateLotLevelPLOptions = {
	transactions: InputTransaction[];
	decimals?: number;
	currentSharePrice?: number;
	normalize?: boolean;
	debug?: boolean;
	nav?: string; // wei format
	closure?: boolean;
	final_share_price?: number;
}

export declare type CloseOut = {
	pnl: number;
	pnl_pu: number;
	currentValueUSD: number;
	openDepositAmount: number;
}


/**
 * Calculates profit and loss for transactions using lot-level accounting (FIFO).
 * @param transactions An array of raw transaction objects.
 * @returns An array of processed transaction objects, with P&L info added to sell transactions.
 */
export function calculateLotLevelPL<T extends InputTransaction = InputTransaction>({
	transactions,
	decimals = 18,
	currentSharePrice = -1,
	normalize = false,
	debug = false,
	nav,
	closure = false,
	final_share_price = 0,
}: CalculateLotLevelPLOptions): { transactions: PnLTransaction<T>[]; closeout: null | CloseOut; } {

	try {

		let transferred = false;

		const bCurrentSharePrice = parseUnits(currentSharePrice.toString(), decimals);
		const sortedTransactions = [...transactions].sort((a, b) => {

			const dateDiff = a.block - b.block;
			if(dateDiff !== 0)
				return dateDiff;

			const vaultDiff = a.vault_address.localeCompare(b.vault_address);
			if(vaultDiff !== 0)
				return vaultDiff;

			// Process buys before sells if they occur at the exact same time for the same ticker
			if (a.trxn_type === "DEPOSIT" && b.trxn_type === "WITHDRAW") return -1;
			if (a.trxn_type === "WITHDRAW" && b.trxn_type === "DEPOSIT") return 1;

			return 0;

		});


		const initialState: ReduceAccumulator = {
			processedTransactions: [],
			openLotsByVault: {},
		};


		const normalizedTransactions = sortedTransactions.map(txn => {
			const t = {
				...txn,
				share_price_usd: parseUnits(txn.share_price_usd.toString(), decimals),
				shares_num: typeof txn.shares === "bigint" ? txn.shares : BigInt(txn.shares.toString()),
			}
			return t;
		});


		const finalState = normalizedTransactions.reduce<ReduceAccumulator>(

			(acc, txn) => {

				const {
					processedTransactions,
					openLotsByVault,
				} = acc;

				const currentVaultOpenLots = openLotsByVault[txn.vault_address]
					? [...openLotsByVault[txn.vault_address]]
					: [];

				if(txn.trxn_type === "DEPOSIT") {

					const newLot: Lot = {
						transaction_hash: txn.transaction_hash,
						chain: txn.chain,
						block: txn.block,
						log_index: txn.log_index,
						originalTxId: txn.transaction_hash,
						vault_address: txn.vault_address,
						quantityRemaining: txn.shares_num,
						purchasePrice: txn.share_price_usd,
						purchaseBlock: txn.block,
					};

					currentVaultOpenLots.push(newLot);

					// For deposits, set pnl to 0
					const newProcessedTx = {
						...txn,
						pnl: 0n,
						unrealizedGain: 0n,
					};

					return {
						processedTransactions: [...processedTransactions, newProcessedTx],
						openLotsByVault: {
							...openLotsByVault,
							[txn.vault_address]: currentVaultOpenLots,
						},
					};

				}

				if(txn.trxn_type === "WITHDRAW") {

					let quantityToCover = txn.shares_num;
					let totalCostDollars = 0n;
					let totalCostPerShare = 0n;
					const contributingLotIds: { hash:string; quantity:bigint; basis:bigint; basis_pu:bigint; realizedGain:bigint; unrealizedGain:bigint; currentValue:bigint; }[] = [];
					let updatedOpenLotsForVault: Lot[] = [];

					// Iterate through available lots for this vault (FIFO order)
					while(quantityToCover > 0 && currentVaultOpenLots.length > 0) {

						const activeLot = currentVaultOpenLots.shift();
						const quantityFromThisLot = bigintMin(quantityToCover, activeLot.quantityRemaining);

						if(quantityFromThisLot === 0n)
							continue;

						const thisCostDollars = (quantityFromThisLot * activeLot.purchasePrice) / BigInt(10**decimals);
						const thisPaidDollars = (quantityFromThisLot * txn.share_price_usd) / BigInt(10**decimals);
						totalCostDollars += thisCostDollars;

						const thisCostPerShare = (thisCostDollars * BigInt(10**decimals)) / quantityFromThisLot || 0n;
						totalCostPerShare += thisCostPerShare

						const realizedGain = BigInt(thisPaidDollars - thisCostDollars);

						contributingLotIds.push({
							hash: activeLot.transaction_hash,
							quantity: quantityFromThisLot,
							basis: thisCostDollars,
							basis_pu: thisCostPerShare,
							realizedGain,
							unrealizedGain: 0n,
							currentValue: 0n,
						});

						quantityToCover -= quantityFromThisLot;

						// If the lot is partially consumed, add the remainder back.
						// This remainder will be the new state of this lot.
						if(activeLot.quantityRemaining > quantityFromThisLot) {
							updatedOpenLotsForVault = [
								{ ...activeLot, quantityRemaining: activeLot.quantityRemaining - quantityFromThisLot, },
								...updatedOpenLotsForVault,
							];
						}

						// If fully consumed, it's not added back to updatedOpenLotsForVault.
					}

					// Any lots remaining in currentVaultOpenLots were not touched by this sale; add them after partially consumed ones.
					updatedOpenLotsForVault.push(...currentVaultOpenLots);

					const sharesSoldFromLots = txn.shares_num - quantityToCover;

					if(quantityToCover > 0) {
						// This means the sale quantity exceeded available shares in lots.
						// P/L will be calculated only for the shares that could be matched.
						console.warn(`Transaction ${txn.transaction_hash} (${txn.vault_address}): Sale quantity ${txn.shares_num} exceeds available shares in lots. ${quantityToCover} shares could not be covered. P/L calculated for the ${sharesSoldFromLots} covered shares only.`);
					}

					// Calculate sale proceeds and realized P/L for the shares matched with lots.
					const saleProceedsFromMatchedShares = sharesSoldFromLots * txn.share_price_usd;
					const pnl = sharesSoldFromLots > 0
						? saleProceedsFromMatchedShares - totalCostDollars
						: 0n;


					const processedSaleTx = {
						...txn,
						pnl,
						costBasisOfSharesSold: sharesSoldFromLots > 0 ? totalCostDollars : 0n,
						appliedLots: contributingLotIds,
						unrealizedGain: 0n,
					};

					return {
						processedTransactions: [...processedTransactions, processedSaleTx],
						openLotsByVault: {
							...openLotsByVault,
							[txn.vault_address]: updatedOpenLotsForVault,
						},
					};

				}

				if(txn.trxn_type === "TRANSFER") {
					transferred = true;
					return acc;
				}

				if(txn.trxn_type === "CLAIM") {
					return acc;
				}

				throw new Error("Invalid transaction type");

			},

			initialState,

		);


		finalState.processedTransactions = finalState.processedTransactions.map(t => {

			if(finalState.openLotsByVault[t.vault_address]) {
				const open = finalState.openLotsByVault[t.vault_address].find(l => l.transaction_hash === t.transaction_hash);
				t.remainder = open ? open.quantityRemaining : 0n;
				t.currentValue = BigInt(formatUnits(t.remainder * bCurrentSharePrice, decimals).split(".")[0]);
			}
			else {
				t.remainder = 0n;
				t.currentValue = 0n;
			}

			if(t.trxn_type === "DEPOSIT") {

				const withdrawals = finalState.processedTransactions.filter(f => f.appliedLots?.find(l => l.hash === t.transaction_hash));

				let received = 0n;
				let paid =  0n; //BigInt(t.amount_brkt);
				let total_paid = withdrawals.length ? 0n : (t.share_price_usd * t.shares_num) / BigInt(10**decimals);
		
				const su = Number(t.shares_num) / 10**decimals;
				const spu = Number(t.share_price_usd) / 10**decimals;
				const basis = BigInt(Math.floor(su * spu * (10**decimals)));

				for(const w of withdrawals) {

					const txn =	w.appliedLots.find(l => l.hash === t.transaction_hash);
					received += BigInt(Math.floor(formatUnits(txn.quantity * w.share_price_usd, decimals)));
					paid += BigInt(Math.floor(formatUnits(txn.quantity * t.share_price_usd, decimals)));
					total_paid += (txn.quantity + t.remainder) * t.share_price_usd / BigInt(10**decimals);

				}

				const unrealizedGain = BigInt(Math.floor((Number(t.remainder) / (10**decimals)) * (Number(bCurrentSharePrice) - Number(t.share_price_usd))));
				const realizedGain = received - paid;
				const pnl = unrealizedGain + realizedGain;
				const paid_pu = t.share_price_usd;
				const pnl_pu = pnl !== 0n && t.shares_num > 0 ? (pnl/t.shares_num) * BigInt(10**decimals) : 0n;

				t.pnl = pnl;
				t.pnl_pu = pnl_pu;
				t.unrealizedGain = unrealizedGain;
				t.realizedGain = realizedGain;
				t.basis = basis;
				t.basis_closed = paid;
				t.basis_open = withdrawals.length ? formatUnits((t.remainder*t.share_price_usd).toString(), decimals).split(".")[0] : total_paid;
				t.basis_pu = paid_pu;
				t.open = t.remainder;

			}

			if(t.trxn_type === "WITHDRAW") {

				const unrealizedGain = t.remainder * bCurrentSharePrice;
				const realizedGain = t.appliedLots.reduce((acc, lot) => acc += lot.realizedGain, 0n);

				const share_price_usd = Number(formatUnits(t.share_price_usd, decimals));
				const pnl = realizedGain + unrealizedGain;
				const pnl_pu = pnl * BigInt(10**decimals) /t.shares_num;

				const paid = t.appliedLots.reduce((acc, lot) => acc + lot.basis || 0n, 0n);
				const paid_pu = parseUnits((Number(paid) / Number(t.shares_num)).toString(), decimals);

				t.pnl = pnl;
				t.pnl_pu = pnl_pu;
				t.unrealizedGain = unrealizedGain;
				t.realizedGain = realizedGain;
				t.basis = paid;
				t.open = t.remainder;
				t.basis_pu = paid_pu;
				t.basis_closed = BigInt(0);
				t.basis_open = BigInt(0);
			}

			return t;

		}).sort((a, b) => a.block > b.block ? -1 : 1 );


		if(debug)
			console.log("currentSharePrice", currentSharePrice);


		if(!normalize) {
			if(debug) {
				console.table(finalState.processedTransactions.map(t => ({
					block: t.block,
					// vault: t.vault_address,
					// hash: t.transaction_hash,
					type: t.trxn_type,
					shares: t.shares,
					shares_num: t.shares_num,
					share_price_usd: t.share_price_usd,
					basis: t.basis,
					basis_pu: t.basis_pu,
					basis_closed: t.basis_closed,
					basis_open: t.basis_open,
					open: t.open,
					total: t.total,
					currentValue: t.currentValue,
					unrealizedGain: t.unrealizedGain,
					realizedGain: t.realizedGain,
					pnl: t.pnl,
					pnl_pu: t.pnl_pu,
				})).sort((a, b) => a.block - b.block));
			}
			return finalState.processedTransactions;
		}

		let result = finalState.processedTransactions.map(t => {

			return {
				...t,
				remainder: Number(formatUnits(t.remainder, decimals)),
				pnl: Number(formatUnits(t.pnl, decimals)),
				pnl_pu: Number(formatUnits(t.pnl_pu, decimals)),
				realizedGain: Number(formatUnits(t.realizedGain, decimals)),
				unrealizedGain: Number(formatUnits(t.unrealizedGain, decimals)),
				basis: Number(formatUnits(t.basis, decimals)),
				basis_pu: Number(formatUnits(t.basis_pu, decimals)),
				basis_closed: Number(formatUnits(t.basis_closed, decimals)),
				basis_open: Number(formatUnits(t.basis_open, decimals)),
				share_price_usd: Number(formatUnits(t.share_price_usd, decimals)),
				shares_num: Number(formatUnits(t.shares_num, decimals)),
				currentValue: Number(formatUnits(t.currentValue, decimals)),
				open: Number(formatUnits(t.open, decimals)),
				price_eth: t.price_eth,
			}

		});


		if(transferred) {

			// TODO: allow for a variable,
			const SHARE_PRICE_AT_TRANSFER = 4095.964108600793;

			const open_shares = result.reduce((acc, t) => {
				return t.trxn_type === "DEPOSIT" ? acc + t.open : t.trxn_type === "WITHDRAW" ? acc - t.open : acc;
			}, 0);

			// this is the basis of the open lots at the time of transfer
			const open_basis = result.reduce((acc, t) => {
				return t.trxn_type === "DEPOSIT" ? acc + t.basis : acc;
			}, 0);

			const orig_open_basis = result.reduce((acc, t) => {
				return t.trxn_type === "DEPOSIT" ? acc + t.basis : acc;
			}, 0);

			const sell_basis = open_shares * SHARE_PRICE_AT_TRANSFER;
			const pnl = sell_basis - open_basis;
			const pnl_pu = pnl / open_shares;

			result = result.map(t => {
				if(t.trxn_type === "DEPOSIT") {
					t.currentValue = 0;
					t.open = 0;
					t.unrealizedGain = 0;
					t.pnl = 0;
				}
				return t;
			})

			const transferRow = {
				amount_brkt: rollup({ key: "amount_brkt", data: result, type: "WEI", }),
				amount_eq: rollup({ key: "amount_eq", data: result, type: "FLOAT", }),
				basis: sell_basis, //rollup({ key: "basis", data: result, type: "FLOAT", }),
				basis_closed: rollup({ key: "basis_closed", data: result, type: "FLOAT", }),
				basis_open: rollup({ key: "basis_open", data: result, type: "FLOAT", }),
				basis_pu: sell_basis / open_shares, //rollup({ key: "basis_pu", data: result, type: "FLOAT", }),
				block: Infinity,
				brkt_claimed: true,
				brkteth_conv: 1.001638147673095,
				chain: 1,
				created: "2025-08-20T18:00:00.000Z",
				currentValue: 0,
				epoch_available: 0,
				finalized: false,
				hash: null,
				log_index: 151,
				nav: nav || 1e18.toString(),
				open: 0, //open: 2.11527,
				pnl,
				pnl_pu,
				price_btc: 96058.65000000001,
				price_eth: 2614.8105,
				realizedGain: pnl,
				remainder: 2.11527,
				salt: "0x0000000000000000000000000000000000000000000000000000000000000000",
				scheduled_time: null,
				share_price_usd: SHARE_PRICE_AT_TRANSFER,
				shares: BigInt(open_shares * (10**decimals)).toString(),
				shares_num: open_shares,
				transaction_hash: "0x0000000000000000000000000000000000000000000000000000000000000000",
				trxn_type: "TRANSFER",
				unrealizedGain: 0,
				vault_address: "0x7A859B5aa0E8294be505af730Ec75E81B14d2788",
			};

			return { transactions: [ transferRow, ...result ], closeout: { pnl, pnl_pu, currentValueUSD: 0, openDepositAmount: open_basis, origDepositAmount: open_basis, }, };

		}

		if(debug) {

			console.table(result.map(t => ({
				block: t.block,
				type: t.trxn_type,
				shares: t.shares,
				shares_num: t.shares_num,
				open: t.open,
				share_price_usd: t.share_price_usd,
				basis: t.basis,	
				basis_pu: t.basis_pu,
				basis_closed: t.basis_closed,
				basis_open: t.basis_open,

				currentValue: t.currentValue,
				unrealizedGain: t.unrealizedGain,
				realizedGain: t.realizedGain,
				pnl: t.pnl,
				price_eth: t.price_eth,
			})).sort((a, b) => a.block - b.block));

		}

		return { transactions: result, closeout: null, };

	}
	catch(error) {

		console.error("Error calculating lot level P&L:", error);
		console.trace();
		throw error;
	}

}

declare type RollupWEIOptions = {
	key: keyof PnLTransaction;
	data: PnLTransaction[];
	type: "WEI"|"FLOAT";
}

const rollup = ({ key, data, type, }): PnLTransaction => {

	switch(type) {

		case "WEI": {
			const wei = data.reduce((acc, t) => {
				if(t.trxn_type === "DEPOSIT") {
					return acc + BigInt(t[key]);
				}
				if(t.trxn_type === "WITHDRAW") {
					return acc - BigInt(t[key]);
				}
				return acc;
			}, 0n);
			return wei.toString();
		}

		case "FLOAT": {
			const float = data.reduce((acc, t) => {
				if(t.trxn_type === "DEPOSIT") {
					return acc + Number(t[key]);
				}
				if(t.trxn_type === "WITHDRAW") {
					return acc - Number(t[key]);
				}
				return acc;
			}, 0);
			return float;
		}

		default:
			throw new Error(`Invalid type: ${type}`);
	}


}
